import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-slate-950/72 backdrop-blur-[6px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-[24px] border border-[#e5e7eb] bg-white p-0 text-[#1f2937] shadow-[0_30px_90px_rgba(15,23,42,0.26)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] dark:border-[#2a303d] dark:bg-[#0f1117] dark:text-[#e5e7eb] dark:shadow-[0_30px_90px_rgba(0,0,0,0.5)] [&>:not([data-dialog-header]):not([data-dialog-footer]):not([cmdk-root])]:px-6 [&>:not([data-dialog-header]):not([data-dialog-footer]):not([cmdk-root])]:pb-6",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 box-border flex !h-8 !w-8 min-w-0 appearance-none items-center justify-center rounded-full border border-white/70 bg-white p-0 text-[#065f46] shadow-[0_8px_24px_rgba(0,0,0,0.16)] opacity-100 transition-all hover:scale-105 hover:bg-[#ecfdf5] focus:outline-none focus:ring-2 focus:ring-[#10b981]/30 disabled:pointer-events-none dark:border-white/15 dark:bg-[#1d2230] dark:text-emerald-100 dark:hover:bg-[#272d3b]">
        <span aria-hidden="true" className="absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 text-[15px] font-bold leading-none">X</span>
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    data-dialog-header=""
    className={cn("rounded-t-[24px] bg-gradient-to-r from-[#059669] to-[#065f46] px-6 pb-5 pt-6 text-white", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    data-dialog-footer=""
    className={cn("flex flex-col-reverse gap-2 border-t border-[#e5e7eb] bg-[#f8f8f8] px-6 py-4 sm:flex-row sm:justify-end dark:border-[#2a303d] dark:bg-[#151922]", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("pr-9 text-lg font-bold leading-6 tracking-[-0.03em] text-white", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("mt-1 text-sm text-white/72", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
